// UTILS
import { ref, inject } from "vue"
import { defineStore, storeToRefs } from "pinia"
import { useToast } from "vue-toastification"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import Bugsnag from "@bugsnag/js"

// TYPES
import type { Answers, TItem } from "@/features/swipe/store/swipe.types.js"
import type { Translate } from "@/plugins/i18n/types"

// SWIPE
import {
  initAnswers,
  initExtra,
  factoryTest,
} from "@/features/swipe/store/swipe.data.js"
import { swipeActor } from "../machines/swipeMachine"

// STORES
import { useI18nStore } from "@/stores/i18n"
import { useUserStore } from "@/stores/user"
import { useQuestionsStore } from "@/features/swipe/store/questions.store"

// API
import {
  patchSwipe,
  getSwipeActive,
  postSwipe,
  deleteSwipe,
} from "@/api/viktor/assessment/assessment"

// HELPERS
import { getVersionFromLang } from "@/features/swipe/helpers/getVersionFromLang"
import { sleep } from "@/lib/utils"

export const useAnswersStore = defineStore("swipeAnswersStore", () => {
  const t = inject("t") as Translate
  const { setCurrentIndex } = useQuestionsStore()
  const i18nStore = useI18nStore()
  const { updateLocale, getLocaleIdFromIsoCode } = i18nStore
  const { nextAssessment } = storeToRefs(useUserStore())
  const { currentLocale } = storeToRefs(useI18nStore())
  const { user, isRecruiter } = storeToRefs(useUserStore())

  const answers = ref<Answers>(initAnswers)
  const isPatchExtraLoading = ref(false)

  const userStore = useUserStore()
  const { isFromInt } = storeToRefs(useUserStore())

  const toast = useToast()

  //-- FETCH AND SET SWIPE

  async function fetchSwipe() {
    const swipeAssessment = await getSwipeActive()
    const isSwipeInit = swipeAssessment?.is_active === true
    const isFinished = swipeAssessment?.status === "finish"

    const cannotRestart =
      !swipeAssessment?.can_restart && // legacy
      !swipeAssessment?.retake_test && // legacy
      nextAssessment.value?.next_assessment.assessment === "swipe" &&
      !nextAssessment.value?.next_assessment.retake
    const canRestart =
      swipeAssessment?.retake_test || // legacy
      swipeAssessment?.can_restart || // legacy
      (nextAssessment.value?.next_assessment.assessment === "swipe" &&
        nextAssessment.value?.next_assessment.retake)

    if (isFinished && cannotRestart && !swipeAssessment.not_passed) {
      // si le test est terminé, on sort de l'assessment
      if (isFromInt.value) answers.value.status = "thank-you"
      else window.location.reload()
    } else if (swipeAssessment?.status === null || !isSwipeInit) {
      // si le test n'a jamais été commencé on l'initalise
      await initSwipe(false, null)
    } else if (isFinished && canRestart) {
      // si le status est finish et qu'on est soit sur une demande de retake test pour PE
      // ou sur un test datant de plus d'un an
      // si on a une demande de retake_test
      await deleteSwipe()
      await initSwipe(false, null)
    } else if (
      swipeAssessment?.status === "todo" ||
      swipeAssessment?.status === "inprogress" ||
      !!swipeAssessment?.not_passed
    ) {
      // autrement c'est que le test est en cours
      // donc on récupère les données du test et on popule les stores
      const swipeData = await syncAssessment()
      answers.value = {
        ...initAnswers,
        ...swipeData,
        data: {
          ...initAnswers.data,
          ...(typeof swipeData.data === "object" &&
          !Array.isArray(swipeData.data)
            ? swipeData.data
            : {}),
        },
      }
      if (Object.keys(answers.value?.data || {}).length === 0) {
        setCurrentIndex(0)
        answers.value.data.extra = initAnswers.data.extra
      } else {
        setCurrentIndex(answers.value?.data?.items?.length)
      }

      // sometimes version was not set in the assessment
      let versionFull
      if (answers.value?.version === null) {
        versionFull = getVersionFromLang(currentLocale.value.iso_code)
        answers.value.version = versionFull
      } else {
        versionFull = answers.value.version
      }

      const lastUnderscoreIndex = versionFull.lastIndexOf("_")
      const langCode = versionFull.substring(0, lastUnderscoreIndex)
      userStore.audio = answers.value.data?.extra?.general_audio ? true : false
      answers.value.data.extra.elapsed_time = swipeData.elapsed_time
      answers.value.data.extra.finished = swipeData.finished_at ? true : false
      answers.value.data.extra.native_language = swipeData.native_language
      answers.value.data.extra.wholesome_conditions =
        swipeData.wholesome_conditions

      if (
        langCode !== currentLocale.value.iso_code &&
        answers.value?.status === "inprogress"
      ) {
        const locale_id = getLocaleIdFromIsoCode(langCode)
        await updateLocale(locale_id)
        // @TODO: set trad key from luciano
        // toast.info("Language change")
      }
    }

    return answers.value
  }

  /**
   * Get active answer index
   * @returns Active answer index
   */
  function getActiveAnswer() {
    return answers.value.data.extra.current || 0
  }

  /**
   * Answer a question
   * @param id Question ID
   * @param answer Question answer
   * @returns
   */
  async function answerQuestion(id: number, answer: TItem) {
    if (answers.value.data.items.length < 76) {
      answers.value.data.items[id] = answer
    } else {
      console.warn("too many answers")
      return
    }
  }

  //-- TRIAL

  /**
   * Patch trial
   * @param id
   * @param data
   */
  async function patchTrial(
    id: number,
    data: { time: number; errors: number }
  ) {
    const step = `step_${id + 1}` as "step_1" | "step_2" | "step_3"

    if (!answers.value.data.trial) {
      answers.value.data.trial = {
        step_1: { time: 0, errors: 0 },
        step_2: { time: 0, errors: 0 },
        step_3: { time: 0, errors: 0 },
        retries: 0,
        finished: false,
      }
    }

    answers.value.data.trial[step] = data
    await patchSwipeData()
  }

  /**
   * Finish trial
   */
  async function finishTrial() {
    if (answers.value.data.trial) answers.value.data.trial.finished = true
    await patchSwipeData()
    MixpanelInstance.track("swipe_start")
  }

  /**
   * Restart trial
   */
  async function restartTrial() {
    answers.value.data.trial = {
      step_1: { time: 0, errors: 0 },
      step_2: { time: 0, errors: 0 },
      step_3: { time: 0, errors: 0 },
      retries: (answers.value.data.trial?.retries ?? 0) + 1,
      finished: false,
    }
    await patchSwipeData()
  }

  //-- TEST

  /**
   * Current item
   * @param id
   */
  async function setCurrentItem(id: number) {
    answers.value.data.extra.current = id
  }

  /**
   * Swipe back
   * @param id
   * @param answer
   */
  async function swipeBack(id: number, answer: TItem) {
    answers.value.data.items[id] = answer
    await patchSwipeData()
  }

  /**
   * Sync the answers with the backend
   */
  async function syncAssessment() {
    const assessment = await getSwipeActive()
    const currentAnswers = answers.value

    const newLength = assessment?.data?.items?.length ?? 0
    const currentLength = answers.value?.data?.items?.length ?? 0

    answers.value = assessment

    // We don't want to override the answers if the new length is less than the current length
    if (newLength < currentLength) {
      answers.value.data.items = currentAnswers.data.items
    }

    return assessment
  }

  /**
   * Answer a question
   */
  async function patchSwipeData() {
    try {
      const data = {
        version: getVersionFromLang(currentLocale.value.iso_code),
        is_completed: answers.value.is_completed,
        locale_id: currentLocale.value.id,
        status: answers.value.status,
        data: answers.value.data,
      }
      await patchSwipe(data)
    } catch (error) {
      Bugsnag.notify(error, (event) => {
        event.context = "patchSwipeData in answers store"
        event.severity = "error"
        event.unhandled = false
        event.addMetadata("request", {
          endpoint: "patchSwipe",
          dataSize: JSON.stringify(answers.value?.data).length,
        })
        event.addMetadata("full_data", {
          answersData: JSON.stringify(answers.value?.data),
        })
      })
    }
  }

  /**
   * Patch tutorial time
   * @param id Tutorial step ID
   * @param val Time to add
   */
  async function patchTutorial(id: number, val: number) {
    const step = `step_${id + 1}`

    if (!answers.value.data.tutorial_time) {
      answers.value.data.tutorial_time = {
        step_1: 0,
        step_2: 0,
        step_3: 0,
        step_4: 0,
        finished: false,
      }
    }

    answers.value.data.tutorial_time[step] =
      answers.value.data.tutorial_time[step] + val
    await patchSwipeData()
  }

  /**
   * Patch extra data
   * @param type Extra data type
   * @param answer Extra data answer
   */
  async function patchExtra(type: string, answer: boolean) {
    isPatchExtraLoading.value = true
    try {
      answers.value.data.extra[type] = answer

      if (type === "wholesome_conditions") {
        answers.value.data.extra.elapsed_time = getElapsedTime()
        // si langue natale + conditions de wholesome sont true, c'est la fin du test, pas de restart
        if (answers.value.data.extra.native_language && answer) {
          answers.value.data.extra.isRestarting = false
          await isTheEnd()
        } else if (answers.value.data.extra.isRestarting) {
          await isTheEnd()
        } else if (userStore.isFromInt.value) {
          await isTheEnd()
        }
      }
      await patchSwipeData()
    } catch (error) {
      console.log("Error while patching extra data", error)
      toast.error(t("general.error.general"))
      throw error
    } finally {
      await sleep(300) // Timeout to prevent flicker
      isPatchExtraLoading.value = false
    }
  }

  async function finishTutorial() {
    if (!answers.value.data.tutorial_time) {
      answers.value.data.tutorial_time = {
        step_1: 0,
        step_2: 0,
        step_3: 0,
        step_4: 0,
        finished: false,
      }
    }

    answers.value.data.tutorial_time.finished = true
    await patchSwipeData()
  }

  /**
   * Finish questions
   */
  async function finishQuestions() {
    answers.value.data.extra.all_items_completed = true
    await patchSwipeData()
    swipeActor.send({ type: "done" })
  }

  /**
   * Mark assessment as finished
   */
  async function isTheEnd() {
    answers.value.data.extra.finished = true
    answers.value.status = "finish"

    MixpanelInstance.track("swipe_finish", {
      locale: answers.value.locale_id,
      isRecruiter: isRecruiter.value,
      origin: isRecruiter.value ? "invited" : "autonomous",
      data: answers.value.data,
    })
  }

  /**
   * Get elapsed time
   * @returns Elapsed time
   */
  function getElapsedTime() {
    let time = 0
    answers.value.data.items.map((item) => (time += item.time_initial))
    return Math.round(time)
  }

  /**
   * Init Swipe
   * @param restarting
   * @param lang
   * @returns
   */
  async function initSwipe(restarting?: boolean, lang?: number | null) {
    const payload: Answers = {
      ...initAnswers,
      version: getVersionFromLang(currentLocale.value.iso_code),
      locale_id: lang ?? currentLocale.value.id,
      is_active: answers.value.is_active,
      status: answers.value.status,
      is_completed: answers.value.is_completed,
      user: {
        uuid: user.value.user.uuid,
        first_name: user.value.user.first_name,
        email: user.value.user.email,
        is_recruiter: user.value.user.company_uuid !== null,
        is_guest: user.value.permissions?.includes("int"),
        gender: user.value.user.gender,
      },
      data: {
        ...initAnswers.data,
        extra: {
          ...initExtra,
          isRestarting: restarting ?? null,
        },
      },
      permissions: user.value.permissions,
    }
    const data = await postSwipe(payload)
    answers.value = payload
    setCurrentIndex(0)

    return data
  }

  //-- RESTART

  /**
   * Patch restart
   * @param type Restart type
   * @param answer Restart answer
   * @param lang Language ID
   */
  async function patchRestart(
    type: string,
    answer: boolean,
    lang?: number | null
  ) {
    // then, if user wants to restart
    if (answer) {
      // Submit final results before resetting the assessment
      try {
        answers.value.data.extra.isRestarting = answer
        await isTheEnd()
        await patchSwipeData()
      } catch (error) {
        console.log(error)
        return
      }

      lang && (await updateLocale(lang))
      await resetUserData(true)

      // finally, send user to the right state (now or later)
      if (type === "now") {
        return swipeActor.send({ type: "now" })
      } else {
        return swipeActor.send({ type: "later" })
      }
    } else {
      // SwipeLoadResults will call submitFinalResults() on mount
      return swipeActor.send({ type: "done" })
    }
  }

  /**
   * Submit final results
   * @param lang Language ID
   * @returns
   */
  async function submitFinalResults(lang?: number | null) {
    try {
      await syncAssessment()

      answers.value.data.extra.elapsed_time = getElapsedTime()
      answers.value.data.extra.finished = true

      const payload: Answers = {
        version: answers.value.version,
        locale_id: lang || currentLocale.value.id,
        data: answers.value.data,
      }

      // ultime patch to finish the test
      await patchSwipe(payload)

      MixpanelInstance.track("swipe_finish", {
        payload: payload,
      })
    } catch (error) {
      Bugsnag.notify(error, (event) => {
        event.context = "SwipeAnswerStore.submitFinalResults"
        event.severity = "error"
        event.unhandled = false
        event.addMetadata("request", {
          lang: lang,
          full_payload: {
            version: getVersionFromLang(currentLocale.value.iso_code),
            locale_id: lang || currentLocale.value.id,
            payload: {
              version: answers.value.version,
              locale_id: lang || currentLocale.value.id,
              data: answers.value.data,
            },
          },
        })
      })
    }
  }

  //-- FOR AF QUICK TESTS

  /**
   * Payload to reset user data light
   * @param locale_id Language ID
   * @returns Payload to reset user data light
   */
  const payloadToResetUserDataLight = (locale_id: number) => {
    return {
      version: "fr_FR_1.0.0",
      locale_id: locale_id,
      data: {
        tutorial_time: {
          step_1: 0,
          step_2: 0,
          step_3: 0,
          step_4: 0,
          finished: false,
        },
        trial: {
          step_1: { time: 0, errors: 0 },
          step_2: { time: 0, errors: 0 },
          step_3: { time: 0, errors: 0 },
          retries: 0,
          finished: false,
        },
        items: [],
        extra: {
          all_items_completed: false,
          current: 0,
          native_language: null,
          wholesome_conditions: null,
          elapsed_time: 0,
          finished: false,
          isRestarting: null,
          trigger_events: true,
        },
      },
    }
  }

  /**
   * Reset user data light
   */
  async function resetUserDataLight() {
    const payload = payloadToResetUserDataLight(currentLocale.value.id)
    await patchSwipe(payload)
    window.location.reload()
  }

  /**
   * Payload to reset user data
   * @param version Version
   * @param locale_id Language ID
   * @param isRestarting Is user restarting
   * @returns Payload to reset user data
   */
  const payloadToResetUserData = (
    version: string,
    locale_id: number,
    isRestarting: boolean | null
  ) => {
    return {
      version: version,
      locale_id: locale_id,
      data: {
        tutorial_time: {
          step_1: 0,
          step_2: 0,
          step_3: 0,
          step_4: 0,
          finished: false,
        },
        trial: {
          step_1: { time: 0, errors: 0 },
          step_2: { time: 0, errors: 0 },
          step_3: { time: 0, errors: 0 },
          retries: 0,
          finished: false,
        },
        items: [],
        extra: {
          all_items_completed: false,
          current: 0,
          native_language: null,
          wholesome_conditions: null,
          elapsed_time: 0,
          finished: false,
          isRestarting: isRestarting,
          trigger_events: true,
        },
      },
    }
  }

  /**
   * Reset user data
   * @param isRestarting Is user restarting
   */
  async function resetUserData(isRestarting: boolean | null) {
    await deleteSwipe()

    // restart to save the fact that the user has already passed the test
    // reload : on restart screen, user can choose to restart later so no reloading
    const cleanData = payloadToResetUserData(
      getVersionFromLang(currentLocale.value.iso_code),
      currentLocale.value.id,
      isRestarting
    )
    answers.value.version = cleanData.version
    answers.value.locale_id = cleanData.locale_id
    answers.value.data = cleanData.data
    await postSwipe(cleanData)
  }

  /**
   * Patch factory test
   * @param isRestarting Is user restarting
   */
  async function patchFactoryTest(isRestarting: boolean) {
    const payload = factoryTest(currentLocale.value.id, isRestarting)
    await patchSwipe(payload)
    window.location.reload()
  }

  return {
    answers,
    fetchSwipe,
    getActiveAnswer,
    answerQuestion,
    patchSwipeData,
    resetUserData,
    resetUserDataLight,
    swipeBack,
    patchTutorial,
    patchTrial,
    finishTrial,
    restartTrial,
    finishTutorial,
    finishQuestions,
    setCurrentItem,
    patchExtra,
    isPatchExtraLoading,
    patchRestart,
    submitFinalResults,
    patchFactoryTest,
  }
})
